import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { OrganisationArSettingsSection } from "@/components/OrganisationArSettingsSection";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { UsersTable } from "@/components/UsersTable";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";
import { formatCountryName, formatCurrencyName } from "@/lib/referenceData";
import {
  PencilIcon,
  PlusIcon,
  ExternalLinkIcon,
  InfoIcon,
  ActivityIcon,
  FileTextIcon,
  FileMinusIcon,
  FlaskConicalIcon,
  ReceiptIcon,
  UsersIcon,
  BuildingIcon,
  DollarSignIcon,
  LinkIcon,
  PercentIcon,
  UserSquareIcon,
  PackageIcon,
  AlertTriangleIcon,
  CreditCardIcon,
  CheckCircle2Icon,
  XCircleIcon,
  MessagesSquareIcon,
  MailIcon,
  BellIcon,
  PlugIcon,
  RepeatIcon,
  ShoppingCartIcon,
  TruckIcon,
  GitCompareIcon,
  BarChart3Icon,
  NetworkIcon,
  LandmarkIcon,
  SlidersHorizontalIcon,
  WalletIcon,
} from "lucide-react";
import { ActivityView } from "@/components/activity/ActivityView";
import { ConfirmActionDialog } from "@/components/ConfirmActionDialog";
import { CreateMagicLinkDialog } from "@/components/CreateMagicLinkDialog";
import { EditOrganisationDialog } from "@/components/EditOrganisationDialog";
import { TaxRatesTable } from "@/components/TaxRatesTable";
import { CustomersTable } from "@/components/CustomersTable";
import { ProductsTable } from "@/components/ProductsTable";
import { InvoicesTable } from "@/components/InvoicesTable";
import { BillsTable } from "@/components/BillsTable";
import { ExpensesTable } from "@/components/ExpensesTable";
import { OrganisationEInvoicingSection } from "@/components/OrganisationEInvoicingSection";
import { OrganisationBankConnectionsSection } from "@/components/OrganisationBankConnectionsSection";
import { SeedMatchPracticeDataDialog } from "@/components/SeedMatchPracticeDataDialog";
import { SuppliersTable } from "@/components/SuppliersTable";
import { PurchaseOrdersTable } from "@/components/PurchaseOrdersTable";
import { DeliveryNotesTable } from "@/components/DeliveryNotesTable";
import { MatchStatsPanel } from "@/components/MatchStatsPanel";
import { MatchLinesTable } from "@/components/MatchLinesTable";
import { MatchGroupsTable } from "@/components/MatchGroupsTable";
import { InvoicePaymentsTable } from "@/components/InvoicePaymentsTable";
import { InvoiceEmailsTable } from "@/components/InvoiceEmailsTable";
import { InvoiceRemindersTable } from "@/components/InvoiceRemindersTable";
import { OrganisationCreditNotesTable } from "@/components/OrganisationCreditNotesTable";
import { RecurringInvoiceSchedulesTable } from "@/components/RecurringInvoiceSchedulesTable";
import { AgentConversationsTable } from "@/components/AgentConversationsTable";
import { AgentConversationStats } from "@/components/AgentConversationStats";
import { McpSessionsTable } from "@/components/McpSessionsTable";
import { GmailAuditLogsTable } from "@/components/GmailAuditLogsTable";
import { cn } from "@/lib/utils";
import { useRecordNav } from "@/components/RecordNav";

type Organisation = components["schemas"]["OrganisationAdminResponse"];
type OrgArSettings = components["schemas"]["OrganisationArSettingsResponse"];
type MagicLink = components["schemas"]["MagicLinkResponse"];
type CountrySummary = components["schemas"]["CountryBankProfileSummary"];
type Currency = components["schemas"]["CurrencyResponse"];
type PaymentAccount = components["schemas"]["PaymentAccountResponse"];

const businessTypeLabels: Record<string, string> = {
  SoleTrader: "Sole Trader",
  LimitedCompany: "Limited Company",
  LLP: "LLP",
  Partnership: "Partnership",
};

const employeeCountLabels: Record<string, string> = {
  OneToTen: "1–10",
  ElevenToFifty: "11–50",
  FiftyOneToTwoHundred: "51–200",
  TwoHundredToOneThousand: "200–1,000",
  OneThousandPlus: "1,000+",
};

const annualRevenueLabels: Record<string, string> = {
  Under100k: "Under £100k",
  OneHundredToFiveHundredK: "£100k–£500k",
  FiveHundredKToTwoM: "£500k–£2M",
  TwoMPlus: "£2M+",
};

const avgInvoicesPerMonthLabels: Record<string, string> = {
  OneToTen: "1–10",
  ElevenToFifty: "11–50",
  FiftyToOneHundred: "50–100",
  OneHundredPlus: "100+",
};

const avgInvoiceValueLabels: Record<string, string> = {
  Under500: "Under £500",
  FiveHundredToFiveK: "£500–£5k",
  FiveKToFiftyK: "£5k–£50k",
  Over50k: "Over £50k",
};

const primaryPaymentMethodLabels: Record<string, string> = {
  BankTransfer: "Bank Transfer",
  Card: "Card",
  DirectDebit: "Direct Debit",
  Cash: "Cash",
};

type Section =
  | "info"
  | "analytics"
  | "ar-settings"
  | "payment-account"
  | "users"
  | "magic-links"
  | "tax-rates"
  | "customers"
  | "products"
  | "invoices"
  | "invoice-payments"
  | "invoice-emails"
  | "invoice-reminders"
  | "credit-notes"
  | "recurring-invoices"
  | "bills"
  | "expenses"
  | "e-invoicing"
  | "bank-connections"
  | "suppliers"
  | "purchase-orders"
  | "delivery-notes"
  | "matching"
  | "match-stats"
  | "agent-conversations"
  | "mcp-sessions"
  | "gmail-audit-logs";

const navItems: { id: Section; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "info", label: "Info", icon: InfoIcon },
  { id: "analytics", label: "Analytics", icon: ActivityIcon },
  { id: "ar-settings", label: "AR Settings", icon: SlidersHorizontalIcon },
  { id: "payment-account", label: "Payment Account", icon: CreditCardIcon },
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "magic-links", label: "Magic Links", icon: LinkIcon },
  { id: "tax-rates", label: "Tax Rates", icon: PercentIcon },
  { id: "customers", label: "Customers", icon: UserSquareIcon },
  { id: "products", label: "Products", icon: PackageIcon },
  { id: "invoices", label: "Invoices", icon: FileTextIcon },
  { id: "invoice-payments", label: "Invoice Payments", icon: DollarSignIcon },
  { id: "invoice-emails", label: "Invoice Emails", icon: MailIcon },
  { id: "invoice-reminders", label: "Invoice Reminders", icon: BellIcon },
  { id: "credit-notes", label: "Credit Notes", icon: FileMinusIcon },
  { id: "recurring-invoices", label: "Recurring Invoices", icon: RepeatIcon },
  { id: "bills", label: "Bills", icon: ReceiptIcon },
  { id: "expenses", label: "Expenses", icon: WalletIcon },
  { id: "e-invoicing", label: "E-Invoicing", icon: NetworkIcon },
  { id: "bank-connections", label: "Bank Connections", icon: LandmarkIcon },
  { id: "suppliers", label: "Suppliers", icon: BuildingIcon },
  { id: "purchase-orders", label: "Purchase Orders", icon: ShoppingCartIcon },
  { id: "delivery-notes", label: "Delivery Notes", icon: TruckIcon },
  { id: "matching", label: "Match Groups", icon: GitCompareIcon },
  { id: "match-stats", label: "Match Stats", icon: BarChart3Icon },
  { id: "agent-conversations", label: "Agent Conversations", icon: MessagesSquareIcon },
  { id: "mcp-sessions", label: "MCP Sessions", icon: PlugIcon },
  { id: "gmail-audit-logs", label: "Gmail Audit Logs", icon: MailIcon },
];

const sectionIds = new Set<Section>(navItems.map((item) => item.id));

function readSectionFromHash(): Section {
  const hash = window.location.hash.replace(/^#/, "") as Section;
  return sectionIds.has(hash) ? hash : "info";
}

const paymentAccountStatusVariant: Record<
  NonNullable<PaymentAccount["status"]>,
  "default" | "secondary" | "destructive" | "outline"
> = {
  Active: "default",
  OnboardingInProgress: "secondary",
  NotStarted: "outline",
  Restricted: "secondary",
  Rejected: "destructive",
  Disconnected: "destructive",
};

export function OrganisationPage() {
  const { organisationId } = useParams<{ organisationId: string }>();

  const [section, setSection] = useState<Section>(readSectionFromHash);

  useEffect(() => {
    function onHashChange() {
      setSection(readSectionFromHash());
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  // AR settings are read-only here (bar the PDF template); the organisation edits them via the sandbox.
  const [arSettings, setArSettings] = useState<OrgArSettings | null>(null);
  const [arSettingsError, setArSettingsError] = useState<string | null>(null);
  const [arSettingsReloading, setArSettingsReloading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const [countries, setCountries] = useState<CountrySummary[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [paymentAccount, setPaymentAccount] = useState<PaymentAccount | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetPending, setResetPending] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [seedPracticeOpen, setSeedPracticeOpen] = useState(false);
  // Bumped after seeding so the bills table remounts and refetches rather than showing a stale page.
  const [billsRefreshToken, setBillsRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.GET("/v1/bank-details/countries"),
      api.GET("/v1/currencies/available"),
    ]).then(([countriesRes, currenciesRes]) => {
      if (cancelled) return;
      if (countriesRes.data) setCountries(countriesRes.data);
      if (currenciesRes.data) setCurrencies(currenciesRes.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;

    async function fetchOrganisation() {
      setOrgLoading(true);
      const [orgRes, arRes] = await Promise.all([
        api.GET("/admin/organisations/{id}", { params: { path: { id: organisationId! } } }),
        api.GET("/admin/organisations/{id}/ar-settings", { params: { path: { id: organisationId! } } }),
      ]);
      if (cancelled) return;
      setOrganisation(orgRes.data ?? null);
      setArSettings(arRes.data ?? null);
      setArSettingsError(arRes.data ? null : "Could not load AR settings.");
      setOrgLoading(false);
    }

    fetchOrganisation();
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  // Retry for the AR Settings tab only; the organisation itself loaded fine.
  const reloadArSettings = useCallback(async () => {
    if (!organisationId) return;
    setArSettingsReloading(true);
    const res = await api.GET("/admin/organisations/{id}/ar-settings", {
      params: { path: { id: organisationId } },
    });
    setArSettings(res.data ?? null);
    setArSettingsError(res.data ? null : "Could not load AR settings.");
    setArSettingsReloading(false);
  }, [organisationId]);

  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;

    // Hitting /status triggers the backend's resource_missing detection, so a rotated platform
    // key surfaces as Disconnected here without waiting for another action.
    api
      .GET("/v1/organisations/{id}/payment-account/status", {
        params: { path: { id: organisationId } },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setPaymentAccount(data ?? null);
      });

    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  async function handleResetPaymentAccount() {
    if (!organisationId) return;
    setResetPending(true);
    setResetError(null);
    const { data, error: apiError } = await api.DELETE(
      "/v1/organisations/{id}/payment-account",
      { params: { path: { id: organisationId } } },
    );
    setResetPending(false);
    if (apiError) {
      setResetError(
        (apiError as { detail?: string } | undefined)?.detail ??
          "Failed to reset payment account.",
      );
      return;
    }
    setPaymentAccount(data ?? null);
    setResetDialogOpen(false);
  }

  useRecordNav({
    back: { to: "/organisations", label: "Organisations" },
    kind: "Organisation",
    title: organisation?.name ?? "Organisation",
    items: navItems,
    active: section,
  });

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">
          {organisation?.name ?? "Organisation"}
        </h1>
      </div>

      {paymentAccount?.status === "Disconnected" && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-destructive" />
          <div className="flex-1">
            <p className="font-semibold text-destructive">Payment integration disconnected</p>
            <p className="mt-1 text-sm text-muted-foreground">
              The stored Stripe account
              {paymentAccount.accountId ? (
                <> (<span className="font-mono">{paymentAccount.accountId}</span>)</>
              ) : null}
              {" "}is no longer reachable on the current platform key — typically the platform key was rotated to a different Stripe account.
              Reset the integration to clear the stored account and re-run onboarding.
            </p>
            {resetError && <p className="mt-2 text-sm text-destructive">{resetError}</p>}
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setResetError(null);
              setResetDialogOpen(true);
            }}
          >
            Reset integration
          </Button>
        </div>
      )}

      <div className="flex gap-6">

        <div className="min-w-0 flex-1">
          {section === "info" && (
            <InfoSection
              organisation={organisation}
              orgLoading={orgLoading}
              countries={countries}
              currencies={currencies}
              onEdit={() => setEditOrgOpen(true)}
            />
          )}

          {section === "ar-settings" && organisationId && (
            <OrganisationArSettingsSection
              organisationId={organisationId}
              settings={arSettings}
              loading={orgLoading || arSettingsReloading}
              error={arSettingsError}
              onRetry={() => void reloadArSettings()}
              onSaved={setArSettings}
              countries={countries}
              currencies={currencies}
            />
          )}

          {section === "payment-account" && (
            <PaymentAccountSection paymentAccount={paymentAccount} />
          )}

          {section === "users" && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Users</h2>
              <UsersTable
                organisationId={organisationId}
                organisationName={organisation?.name}
              />
            </section>
          )}

          {section === "magic-links" && organisationId && (
            <MagicLinksSection organisationId={organisationId} />
          )}

          {section === "tax-rates" && organisationId && (
            <section>
              <TaxRatesTable organisationId={organisationId} />
            </section>
          )}

          {section === "customers" && organisationId && (
            <section>
              <CustomersTable organisationId={organisationId} />
            </section>
          )}

          {section === "products" && organisationId && (
            <section>
              <ProductsTable organisationId={organisationId} />
            </section>
          )}

          {section === "invoices" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Invoices</h2>
              <InvoicesTable organisationId={organisationId} />
            </section>
          )}

          {section === "invoice-payments" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Invoice Payments</h2>
              <InvoicePaymentsTable organisationId={organisationId} />
            </section>
          )}

          {section === "invoice-emails" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Invoice Emails</h2>
              <InvoiceEmailsTable organisationId={organisationId} />
            </section>
          )}

          {section === "invoice-reminders" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Invoice Reminders</h2>
              <InvoiceRemindersTable organisationId={organisationId} />
            </section>
          )}

          {section === "credit-notes" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Credit Notes</h2>
              <OrganisationCreditNotesTable organisationId={organisationId} />
            </section>
          )}

          {section === "recurring-invoices" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Recurring Invoice Schedules</h2>
              <RecurringInvoiceSchedulesTable organisationId={organisationId} />
            </section>
          )}

          {section === "bills" && (
            <section>
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="text-lg font-semibold">Bills</h2>
                {organisationId && (
                  <Button variant="outline" size="sm" onClick={() => setSeedPracticeOpen(true)}>
                    <FlaskConicalIcon className="size-4" />
                    Add practice data
                  </Button>
                )}
              </div>
              <BillsTable key={billsRefreshToken} organisationId={organisationId} />
              {organisationId && (
                <SeedMatchPracticeDataDialog
                  organisationId={organisationId}
                  open={seedPracticeOpen}
                  onOpenChange={setSeedPracticeOpen}
                  onSeeded={() => setBillsRefreshToken((token) => token + 1)}
                />
              )}
            </section>
          )}

          {section === "expenses" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Expenses</h2>
              <ExpensesTable organisationId={organisationId} />
            </section>
          )}

          {section === "e-invoicing" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">E-Invoicing</h2>
              <OrganisationEInvoicingSection organisationId={organisationId} />
            </section>
          )}

          {section === "bank-connections" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Bank Connections</h2>
              <OrganisationBankConnectionsSection organisationId={organisationId} />
            </section>
          )}

          {section === "suppliers" && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Suppliers</h2>
              <SuppliersTable organisationId={organisationId} />
            </section>
          )}

          {section === "purchase-orders" && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Purchase Orders</h2>
              <PurchaseOrdersTable organisationId={organisationId} />
            </section>
          )}

          {section === "delivery-notes" && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Delivery Notes</h2>
              <DeliveryNotesTable organisationId={organisationId} />
            </section>
          )}

          {section === "matching" && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Match Groups</h2>
              <MatchGroupsTable organisationId={organisationId} />
            </section>
          )}

          {section === "analytics" && organisationId && (
            <section>
              <h2 className="mb-1 text-lg font-semibold">Analytics</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                What this organisation has created, and when.
              </p>
              <ActivityView organisationId={organisationId} />
            </section>
          )}

          {section === "match-stats" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Match Stats</h2>
              {/* clientId addresses the cache-refresh endpoint only; the read stays organisation-scoped. */}
              <MatchStatsPanel organisationId={organisationId} clientId={organisation?.clientId ?? undefined} />

              <h3 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Lines
              </h3>
              <MatchLinesTable organisationId={organisationId} />
            </section>
          )}

          {section === "agent-conversations" && organisationId && (
            <section className="space-y-6">
              <AgentConversationStats organisationId={organisationId} />
              <div>
                <h2 className="mb-4 text-lg font-semibold">Conversations</h2>
                <AgentConversationsTable organisationId={organisationId} />
              </div>
            </section>
          )}

          {section === "mcp-sessions" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">MCP Sessions</h2>
              <McpSessionsTable organisationId={organisationId} />
            </section>
          )}

          {section === "gmail-audit-logs" && organisationId && (
            <section>
              <h2 className="mb-4 text-lg font-semibold">Gmail Audit Logs</h2>
              <GmailAuditLogsTable organisationId={organisationId} />
            </section>
          )}
        </div>
      </div>

      <EditOrganisationDialog
        organisation={organisation}
        open={editOrgOpen}
        onOpenChange={setEditOrgOpen}
        onUpdated={() => {
          setEditOrgOpen(false);
          if (organisationId) {
            api
              .GET("/admin/organisations/{id}", { params: { path: { id: organisationId } } })
              .then(({ data }) => {
                if (data) setOrganisation(data);
              });
          }
        }}
      />

      <ConfirmActionDialog
        open={resetDialogOpen}
        onOpenChange={(next) => {
          setResetDialogOpen(next);
          if (!next) setResetError(null);
        }}
        title="Reset payment integration?"
        description={
          <>
            This clears the stored Stripe account
            {paymentAccount?.accountId ? (
              <> (<span className="font-mono">{paymentAccount.accountId}</span>)</>
            ) : null}{" "}
            so the organisation can restart onboarding from scratch. Existing payment history is
            preserved. The organisation will not be able to accept payments again until onboarding is
            completed against a new connected account.
          </>
        }
        confirmLabel="Reset integration"
        pending={resetPending}
        onConfirm={handleResetPaymentAccount}
      />
    </div>
  );
}

interface InfoSectionProps {
  organisation: Organisation | null;
  orgLoading: boolean;
  countries: CountrySummary[];
  currencies: Currency[];
  onEdit: () => void;
}

function InfoSection({
  organisation,
  orgLoading,
  countries,
  currencies,
  onEdit,
}: InfoSectionProps) {
  if (orgLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (!organisation) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{organisation.name}</CardTitle>
        <Button variant="outline" size="sm" onClick={onEdit}>
          <PencilIcon className="size-4 mr-1" />
          Edit
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground text-sm">Organisation ID</dt>
            <dd className="mt-1 flex items-center gap-1">
              <Badge variant="secondary" className="font-mono text-xs">
                {organisation.id}
              </Badge>
              {organisation.id && <CopyButton value={organisation.id} />}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Client</dt>
            <dd className="mt-1">
              {organisation.clientId && (
                <Link to={`/clients/${organisation.clientId}`}>
                  <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                    {organisation.clientId}
                  </Badge>
                </Link>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Created</dt>
            <dd className="mt-1 text-sm">{formatDateTime(organisation.createdAt)}</dd>
            {organisation.createdBy && (
              <dd className="mt-1">
                <Link
                  to={`/users/${organisation.createdBy}`}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <Badge variant="secondary" className="font-mono text-xs">
                    {organisation.createdBy.slice(0, 8)}...
                  </Badge>
                  <ExternalLinkIcon className="size-3" />
                </Link>
              </dd>
            )}
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Updated</dt>
            <dd className="mt-1 text-sm">{formatDateTime(organisation.updatedAt)}</dd>
            {organisation.updatedBy && (
              <dd className="mt-1">
                <Link
                  to={`/users/${organisation.updatedBy}`}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <Badge variant="secondary" className="font-mono text-xs">
                    {organisation.updatedBy.slice(0, 8)}...
                  </Badge>
                  <ExternalLinkIcon className="size-3" />
                </Link>
              </dd>
            )}
          </div>
        </dl>

        {(organisation.businessType ||
          organisation.incorporationYear ||
          organisation.employeeCount ||
          organisation.industryDisplayName) && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Business Info
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-3 text-sm">
              {organisation.businessType && (
                <div>
                  <dt className="text-muted-foreground">Business Type</dt>
                  <dd>
                    {businessTypeLabels[organisation.businessType] ?? organisation.businessType}
                  </dd>
                </div>
              )}
              {organisation.incorporationYear && (
                <div>
                  <dt className="text-muted-foreground">Incorporation Year</dt>
                  <dd>{organisation.incorporationYear}</dd>
                </div>
              )}
              {organisation.industryDisplayName && (
                <div>
                  <dt className="text-muted-foreground">Industry</dt>
                  <dd>
                    {organisation.industryCode ? `${organisation.industryCode} - ` : ""}
                    {organisation.industryDisplayName}
                  </dd>
                </div>
              )}
              {organisation.employeeCount && (
                <div>
                  <dt className="text-muted-foreground">Employees</dt>
                  <dd>
                    {employeeCountLabels[organisation.employeeCount] ?? organisation.employeeCount}
                  </dd>
                </div>
              )}
              {organisation.vatRegistered != null && (
                <div>
                  <dt className="text-muted-foreground">VAT Registered</dt>
                  <dd>{organisation.vatRegistered ? "Yes" : "No"}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {(organisation.contactEmail || organisation.phone || organisation.website) && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Contact
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-3 text-sm">
              {organisation.contactEmail && (
                <div>
                  <dt className="text-muted-foreground">Email</dt>
                  <dd>{organisation.contactEmail}</dd>
                </div>
              )}
              {organisation.phone && (
                <div>
                  <dt className="text-muted-foreground">Phone</dt>
                  <dd>{organisation.phone}</dd>
                </div>
              )}
              {organisation.website && (
                <div>
                  <dt className="text-muted-foreground">Website</dt>
                  <dd>{organisation.website}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {(organisation.addressLine1 || organisation.city) && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Address
            </h3>
            <p className="text-sm">
              {[
                organisation.addressLine1,
                organisation.addressLine2,
                organisation.city,
                organisation.postalCode,
                formatCountryName(organisation.country, countries),
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
          </div>
        )}

        {(organisation.annualRevenue ||
          organisation.avgInvoicesPerMonth ||
          organisation.avgInvoiceValue ||
          organisation.primaryPaymentMethod) && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Financial Profile
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 text-sm">
              {organisation.annualRevenue && (
                <div>
                  <dt className="text-muted-foreground">Annual Revenue</dt>
                  <dd>
                    {annualRevenueLabels[organisation.annualRevenue] ?? organisation.annualRevenue}
                  </dd>
                </div>
              )}
              {organisation.avgInvoicesPerMonth && (
                <div>
                  <dt className="text-muted-foreground">Invoices / Month</dt>
                  <dd>
                    {avgInvoicesPerMonthLabels[organisation.avgInvoicesPerMonth] ??
                      organisation.avgInvoicesPerMonth}
                  </dd>
                </div>
              )}
              {organisation.avgInvoiceValue && (
                <div>
                  <dt className="text-muted-foreground">Avg Invoice Value</dt>
                  <dd>
                    {avgInvoiceValueLabels[organisation.avgInvoiceValue] ??
                      organisation.avgInvoiceValue}
                  </dd>
                </div>
              )}
              {organisation.primaryPaymentMethod && (
                <div>
                  <dt className="text-muted-foreground">Payment Method</dt>
                  <dd>
                    {primaryPaymentMethodLabels[organisation.primaryPaymentMethod] ??
                      organisation.primaryPaymentMethod}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* The parent group whose inbox this site's bills can arrive in. */}
        {organisation.companyId && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Company
            </h3>
            <Link to={`/companies/${organisation.companyId}`}>
              <Badge
                variant="secondary"
                className="font-mono text-xs hover:bg-primary/20"
                title={organisation.companyId}
              >
                {organisation.companyId.slice(0, 8)}...
              </Badge>
            </Link>
          </div>
        )}

        {(organisation.taxNumber || organisation.companyRegistrationNumber) && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Legal
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 text-sm">
              {organisation.taxNumber && (
                <div>
                  <dt className="text-muted-foreground">VAT Number</dt>
                  <dd>{organisation.taxNumber}</dd>
                </div>
              )}
              {organisation.companyRegistrationNumber && (
                <div>
                  <dt className="text-muted-foreground">Company Reg.</dt>
                  <dd>{organisation.companyRegistrationNumber}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {(organisation.primaryColour || organisation.logoUrl) && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Branding
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 text-sm">
              {organisation.primaryColour && (
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Colour</dt>
                  <dd className="flex items-center gap-2">
                    <div
                      className="h-5 w-5 rounded border"
                      style={{ backgroundColor: organisation.primaryColour }}
                    />
                    <span className="font-mono text-xs">{organisation.primaryColour}</span>
                  </dd>
                </div>
              )}
              {organisation.logoUrl && (
                <div>
                  <dt className="text-muted-foreground">Logo</dt>
                  <dd className="truncate max-w-xs">{organisation.logoUrl}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {organisation.defaultCurrency && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Preferences
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Currency</dt>
                <dd>{formatCurrencyName(organisation.defaultCurrency, currencies)}</dd>
              </div>
            </dl>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface PaymentAccountSectionProps {
  paymentAccount: PaymentAccount | null;
}

function PaymentAccountSection({ paymentAccount }: PaymentAccountSectionProps) {
  if (!paymentAccount) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }

  const status = paymentAccount.status;
  const capabilities = paymentAccount.capabilities;
  const currentlyDue = capabilities?.currentlyDue ?? [];
  const pastDue = capabilities?.pastDue ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Payment Account</CardTitle>
        {status && (
          <Badge variant={paymentAccountStatusVariant[status] ?? "secondary"}>
            {status}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground text-sm">Provider</dt>
            <dd className="mt-1 text-sm">
              {paymentAccount.provider ?? <span className="text-muted-foreground">—</span>}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Account ID</dt>
            <dd className="mt-1 flex items-center gap-1">
              {paymentAccount.accountId ? (
                <>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {paymentAccount.accountId}
                  </Badge>
                  <CopyButton value={paymentAccount.accountId} />
                </>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Enabled At</dt>
            <dd className="mt-1 text-sm">
              {paymentAccount.enabledAt ? (
                formatDateTime(paymentAccount.enabledAt)
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </dd>
          </div>
        </dl>

        {capabilities && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Capabilities
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-3 text-sm">
              <CapabilityRow label="Charges Enabled" value={capabilities.chargesEnabled} />
              <CapabilityRow label="Payouts Enabled" value={capabilities.payoutsEnabled} />
              <CapabilityRow label="Details Submitted" value={capabilities.detailsSubmitted} />
            </dl>
            {capabilities.disabledReason && (
              <p className="mt-3 text-sm text-destructive">
                <span className="text-muted-foreground">Disabled Reason: </span>
                {capabilities.disabledReason}
              </p>
            )}
          </div>
        )}

        {(currentlyDue.length > 0 || pastDue.length > 0) && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Requirements
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
              {currentlyDue.length > 0 && (
                <div>
                  <dt className="text-muted-foreground mb-1">Currently Due</dt>
                  <ul className="list-disc pl-5 space-y-1">
                    {currentlyDue.map((item) => (
                      <li key={`cur-${item}`} className="font-mono text-xs">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pastDue.length > 0 && (
                <div>
                  <dt className="text-destructive mb-1">Past Due</dt>
                  <ul className="list-disc pl-5 space-y-1">
                    {pastDue.map((item) => (
                      <li key={`past-${item}`} className="font-mono text-xs text-destructive">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CapabilityRow({ label, value }: { label: string; value: boolean | undefined }) {
  return (
    <div className="flex items-center gap-2">
      {value ? (
        <CheckCircle2Icon className="size-4 text-green-600" />
      ) : (
        <XCircleIcon className="size-4 text-muted-foreground" />
      )}
      <span>{label}</span>
    </div>
  );
}

interface MagicLinksSectionProps {
  organisationId: string;
}

function MagicLinksSection({ organisationId }: MagicLinksSectionProps) {
  const pagination = useCursorPagination({ initialLimit: 10 });

  const [magicLinks, setMagicLinks] = useState<MagicLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .GET("/magic-links", {
        params: {
          query: {
            OrganisationId: organisationId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError) {
          setError("Failed to load magic links.");
        } else {
          setMagicLinks(data?.data ?? []);
          pagination.setPage({
            nextCursor: data?.meta?.cursors?.next,
            previousCursor: data?.meta?.cursors?.previous,
          });
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organisationId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Magic Links</h2>
        <Button onClick={() => setDialogOpen(true)}>
          <PlusIcon className="size-4" />
          Create Magic Link
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Used At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {magicLinks.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground h-24 text-center">
                  No magic links found.
                </TableCell>
              </TableRow>
            ) : (
              magicLinks.map((link) => (
                <TableRow key={link.id}>
                  <TableCell className="font-medium">
                    {link.label || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {link.id}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDateTime(link.createdAt)}</TableCell>
                  <TableCell>{formatDateTime(link.expiresAt)}</TableCell>
                  <TableCell>{formatDateTime(link.usedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      <CreateMagicLinkDialog
        organisationId={organisationId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => pagination.reset()}
      />
    </section>
  );
}
